import { PropTypes } from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import CreateTemplateModal from "./components/CreateTemplateModal";
import TemplateSelect from "./components/TemplateSelect";
import { getCurrentUserId, isAdminUser } from "./helpers/currentUser";
import useTemplateManager from "./hooks/useTemplateManager";

const EmailTemplateSelect = ({
  subject = "",
  body = "",
  attachments = [],
  applyTemplate = () => false,
  validateEmailTemplate = () => ({}),
}) => {
  const [showCreateModal, setShowCreateModal] = useState(false);

  const isAdmin = isAdminUser();
  const currentUserId = getCurrentUserId();

  const formState = useMemo(
    () => ({ subject, body, attachments }),
    [subject, body, attachments],
  );

  const isDifferent = useCallback(
    (currentFormState, template) =>
      emailTemplateDiffer(currentFormState, template),
    [],
  );

  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    templateChanged,
    setTemplateChanged,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isCopying,
    isSharing,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    copyTemplate,
    shareTemplate,
    unshareTemplate,
    applyTemplate: trackApply,
  } = useTemplateManager({
    apiPath: "/email_templates",
    apiResourceName: "email_template",
    templateType: "email",
    displayNameSingular: "Email Template",
    displayNamePlural: "Email Templates",
    formState,
    isDifferent,
    onTemplateCreated: () => {
      setShowCreateModal(false);
    },
  });

  const handleCreate = (payload) => {
    createTemplate(() => payload);
  };

  const updateEmailTemplate = () => {
    const errors = validateEmailTemplate(true);
    if (Object.keys(errors).length === 0 && selectedTemplate?.value) {
      updateTemplate(() => ({
        id: selectedTemplate.value,
        subject,
        body,
        attachment_ids: attachments.map((a) => a.id),
      }));
    }
  };

  return (
    <>
      <TemplateSelect
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        templateChanged={templateChanged}
        setTemplateChanged={setTemplateChanged}
        isLoading={isLoading}
        isCreating={isCreating}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        isCopying={isCopying}
        isSharing={isSharing}
        updateTemplate={updateEmailTemplate}
        deleteTemplate={deleteTemplate}
        copyTemplate={copyTemplate}
        shareTemplate={shareTemplate}
        unshareTemplate={unshareTemplate}
        trackApply={trackApply}
        applyTemplate={applyTemplate}
        onCreate={() => setShowCreateModal(true)}
        isAdmin={isAdmin}
        currentUserId={currentUserId}
        infoTooltip="Templates save your subject, body, and attachments."
      />
      <CreateTemplateModal
        templateType="email"
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onCreate={handleCreate}
        isCreating={isCreating}
        isAdmin={isAdmin}
        formValues={{ subject, body, attachments }}
      />
    </>
  );
};

EmailTemplateSelect.propTypes = {
  subject: PropTypes.string,
  body: PropTypes.string,
  attachments: PropTypes.arrayOf(PropTypes.shape({})),
  applyTemplate: PropTypes.func,
  validateEmailTemplate: PropTypes.func,
};

/**
 * Pure comparison logic extracted for testability.
 * Returns true when currentFormState differs from the saved template.
 */
export const emailTemplateDiffer = (currentFormState, template) => {
  const subjectChanged = currentFormState.subject !== (template.subject || "");
  const bodyChanged = currentFormState.body !== template.body;

  const currentAttachments = currentFormState.attachments || [];
  const templateAttachments = template.attachments || [];

  const attachmentsChanged =
    currentAttachments.length !== templateAttachments.length ||
    !currentAttachments.every((attachment) =>
      templateAttachments.some((a) => a.id === attachment.id),
    );

  return subjectChanged || bodyChanged || attachmentsChanged;
};

export default EmailTemplateSelect;
